/**
 * Part 20 — Soft upgrade prompt.
 *
 * The "show feature disabled with upgrade overlay" variant we picked over
 * modal-lockout / hide-entirely. The buyer sees what they're missing,
 * with a one-click path to the billing page.
 *
 * Two usage patterns:
 *
 *   1. As a fallback inside PlanGate to wrap an entire feature surface:
 *
 *        <PlanGate minTier="growth" fallback={
 *          <UpgradePrompt requiredTier="growth" feature="AI Insights" />
 *        }>
 *          <AIInsightsPanel />
 *        </PlanGate>
 *
 *   2. As a click-to-trigger inline blocker via the `inline` flag — useful
 *      for nav items the user can SEE but not OPEN on their current plan.
 */
import { useNavigate } from 'react-router-dom'
import { Lock, Sparkles, ArrowRight } from 'lucide-react'

import { PLAN_META, type PlanTier } from '../../services/billing'

interface Props {
  /** The plan tier required to use the feature. */
  requiredTier: PlanTier
  /** Display name of the gated feature ("AI Insights", "Slack integration"). */
  feature: string
  /** Optional one-liner explaining the value. */
  description?: string
  /**
   * When true, render as a small inline card instead of the full overlay.
   * Useful next to a nav link or button.
   */
  inline?: boolean
}

export function UpgradePrompt({
  requiredTier,
  feature,
  description,
  inline = false,
}: Props) {
  const navigate = useNavigate()
  const meta = PLAN_META[requiredTier as string] ?? PLAN_META.growth
  const goBilling = () => navigate(`/billing?plan=${requiredTier}`)

  if (inline) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#f0f4f8] border border-[#bcccdc] rounded text-sm">
        <Lock className="w-4 h-4 text-[#829ab1]" />
        <span className="text-[#486581]">
          {feature} is on {meta.label}.
        </span>
        <button
          onClick={goBilling}
          className="text-[#102a43] font-medium hover:underline"
        >
          Upgrade
        </button>
      </div>
    )
  }

  return (
    <div className="relative bg-white border border-[#bcccdc] rounded-lg overflow-hidden">
      {/* Diagonal-stripe overlay over the feature region */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent, transparent 12px, #f0f4f8 12px, #f0f4f8 24px)',
        }}
      />

      <div className="relative p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#102a43] text-white mb-4">
          <Sparkles className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-semibold text-[#102a43] mb-2">
          {feature} is part of the {meta.label} plan
        </h3>
        {description && (
          <p className="text-sm text-[#486581] mb-4 max-w-md mx-auto">
            {description}
          </p>
        )}
        <div className="text-sm text-[#486581] mb-6">
          {meta.price} · Cancel anytime
        </div>
        <button
          onClick={goBilling}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#102a43] text-white rounded hover:bg-[#243b53]"
        >
          Upgrade to {meta.label}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
